import React, { BaseSyntheticEvent, useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import { Box, Flex, Heading, LinkBox, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import * as Yup from 'yup';
import { InputCustom, ButtonCustom } from '../UI';
import AuthLinks from '../authBottomLinks';
import { useAppDispatch, useAppSelector } from '../../../utils/reduxHooks';
import { sendCodeAction } from '../../../modules/auth/actions';
import { selectIsCodeAuth } from '../../../modules/auth/selectors';

interface CodeInputFormProps {
  phone: string;
  onNext: (val: number) => void;
}

const link1 = '/signUp';
const link2 = '/signUp';
const linkName1 = 'Нет аккаунта?';
const linkName2 = 'Зарегистрироваться';
const isCodeLength = 4;

const validationSchema = Yup.object().shape({
  code: Yup.string()
    .required('Введите код подтверждения')
    .length(isCodeLength, `Код должен содержать ${isCodeLength} символа`),
});

export const CodeInputForm: React.FC<CodeInputFormProps> = ({ phone, onNext }) => {
  const dispatch = useAppDispatch();
  const [timer, setTimer] = useState<number>(60);
  const [validationError, setValidationError] = useState<string | null>(null);
  const code = useAppSelector(selectIsCodeAuth);

  const handleResendCode = async () => {
    try {
      setTimer(60);
      dispatch(sendCodeAction.request(phone));
    } catch (error) {
      console.error('Error resending code:', error);
    }
  };

  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleChangeAndSubmit = (
    e: React.ChangeEvent<HTMLInputElement>,
    handleChange: (e: BaseSyntheticEvent) => void,
    submitForm: () => void,
  ) => {
    handleChange(e);
    setValidationError(null);

    if (e.target.value.length > isCodeLength) {
      setValidationError(`Код должен содержать ${isCodeLength} символа`);
    } else if (e.target.value.length === isCodeLength) {
      if (e.target.value == code) {
        setTimeout(() => {
          submitForm();
          onNext(4);
        }, 1000);
      } else {
        setValidationError('Неверный код подтверждения');
      }
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        width="100%"
        height="100vh"
        padding="101px 0 110px"
        maxW="480px"
        mx="auto"
      >
        <Formik
          initialValues={{ code: '' }}
          validationSchema={validationSchema}
          onSubmit={e => console.log('код подтверждения: ', e)}
        >
          {({ errors, touched, values, handleChange, submitForm }) => (
            <Form>
              <Heading mb={6} fontSize="28px">
                Введите код подтверждения
              </Heading>
              <InputCustom
                label="Код подтверждения"
                name="code"
                value={values.code}
                onChange={e => handleChangeAndSubmit(e, handleChange, submitForm)}
                errorMessage={touched.code && errors.code ? errors.code : validationError}
                placeholder="_ _ _ _"
              />
              <Text mt="20px">Сообщение отправлено на номер {phone}</Text>
              <Flex direction="column" alignItems="flex-start">
                <LinkBox p={0} onClick={() => onNext(1)} textDecor="underline" mb="20px">
                  Изменить номер
                </LinkBox>
                <ButtonCustom
                  variant="withBackground"
                  w="full"
                  type="button"
                  onClick={handleResendCode}
                  disabled={timer > 0}
                >
                  {timer > 0
                    ? `Отправить снова через 00:${timer.toString().padStart(2, '0')}`
                    : 'Отправить'}
                </ButtonCustom>
              </Flex>
            </Form>
          )}
        </Formik>
        <AuthLinks link1Name={linkName1} link1Url={link1} link2Name={linkName2} link2Url={link2} />
      </Box>
    </motion.div>
  );
};
